import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { SetupWizardStep } from '../../leagueSetupWizard';
import {
  TWITCH_OWNER_REPAIR_COPY,
  TWITCH_PARTIAL_COVERAGE_COPY,
  TWITCH_SETUP_INTRO_COPY,
  TWITCH_ZERO_BROADCASTERS_COPY,
} from '../../setupTwitchCopy';
import type { LscLeagueReadiness } from '../../types';
import LiveReadinessCard from '../LiveReadinessCard';
import LeagueSetupStepCard from '../LeagueSetupStepCard';

export interface TwitchLiveStepPanelProps {
  leagueId: string;
  wizardStep: SetupWizardStep;
  readiness: LscLeagueReadiness;
  isOwner: boolean;
  onSetupRefresh: () => void;
}

function twitchReadinessItem(readiness: LscLeagueReadiness) {
  return (readiness.items ?? []).find((item) => item.key === 'eventsub_ready');
}

function twitchLinkedItem(readiness: LscLeagueReadiness) {
  return (readiness.items ?? []).find((item) => item.key === 'twitch_linked');
}

function eligibleBroadcasterCount(readiness: LscLeagueReadiness): number {
  const item = twitchLinkedItem(readiness);
  const metrics = (item?.metrics ?? {}) as Record<string, number | undefined>;
  return Number(metrics.eligible_count ?? 0);
}

const TwitchLiveStepPanel: React.FC<TwitchLiveStepPanelProps> = ({
  leagueId,
  wizardStep,
  readiness,
  isOwner,
  onSetupRefresh,
}) => {
  const eventsubItem = useMemo(() => twitchReadinessItem(readiness), [readiness]);
  const twitchLinked = useMemo(() => twitchLinkedItem(readiness), [readiness]);
  const eligibleCount = useMemo(() => eligibleBroadcasterCount(readiness), [readiness]);

  const zeroBroadcasters =
    eligibleCount === 0 &&
    (twitchLinked?.status === 'blocked' || eventsubItem?.status === 'blocked');

  const partialCoverage =
    !zeroBroadcasters &&
    (twitchLinked?.status === 'warning' || eventsubItem?.status === 'warning');

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="twitch-live-step-panel">
      <LeagueSetupStepCard step={wizardStep} />

      <div className="rounded-lg border border-secondary-700 bg-secondary-900/30 px-4 py-3 text-sm text-secondary-300">
        {TWITCH_SETUP_INTRO_COPY}
      </div>

      {zeroBroadcasters ? (
        <div
          className="rounded-lg border border-red-700/40 bg-red-950/10 px-4 py-3 text-sm text-red-100 flex gap-2"
          data-testid="twitch-zero-broadcasters-blocker"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p>{TWITCH_ZERO_BROADCASTERS_COPY}</p>
        </div>
      ) : null}

      {partialCoverage ? (
        <div
          className="rounded-lg border border-amber-700/40 bg-amber-950/10 px-4 py-3 text-sm text-amber-100 flex gap-2"
          data-testid="twitch-partial-coverage-warning"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p>{TWITCH_PARTIAL_COVERAGE_COPY}</p>
        </div>
      ) : null}

      {!isOwner ? (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/40 px-4 py-3 text-sm text-secondary-300"
          data-testid="twitch-owner-repair-notice"
        >
          {TWITCH_OWNER_REPAIR_COPY}
        </div>
      ) : null}

      <LiveReadinessCard
        leagueId={leagueId}
        isOwner={isOwner}
        onReadinessRefresh={onSetupRefresh}
      />
    </div>
  );
};

export default TwitchLiveStepPanel;
